import { ChangeEvent, Dispatch, FunctionComponent, SetStateAction, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { InputField } from "@/components/InputField/InputField";
import { TextArea } from "@/components/TextArea/TextArea";
import { SelectField, SelectFieldOptions } from "@/components/SelectField/SelectField";
import { outputLanguageOptions } from "@/constants/outputLanguages";
import { useUpdateDeepResearchPromptMutation } from "@/pages/Private/redux/deepResearch/deepResearch.api";

import {
	DeepResearchPrompt,
	Section,
	UpdateDeepResearchPrompt,
	UpdateDeepResearchPromptSchema,
} from "../schema/deep-research-prompt";

import { SectionEditor, SectionFieldError } from "./SectionEditor";

interface UpdateDeepResearchPromptModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	prompt: DeepResearchPrompt;
}

const DEFAULT_LANGUAGE = "en";

const languageOptions: SelectFieldOptions = outputLanguageOptions;

export const UpdateDeepResearchPromptModal: FunctionComponent<
	UpdateDeepResearchPromptModalProps
> = ({ isOpen, setIsOpen, prompt }) => {
	const { t } = useTranslation();
	const [updatePrompt, { isLoading, error }] = useUpdateDeepResearchPromptMutation();

	const getValidOrder = (value: unknown, fallback: number): number =>
		typeof value === "number" && Number.isFinite(value) && value >= 1 ? value : fallback;

	const defaultSections = (prompt.prompt?.sections || []).map((section, sIdx) => ({
		...section,
		order: getValidOrder(section.order, sIdx + 1),
		subsections: section.subsections?.map((sub, subIdx) => ({
			...sub,
			order: getValidOrder(sub.order, subIdx + 1),
		})),
	}));

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { errors, isSubmitted, isSubmitting },
	} = useForm<UpdateDeepResearchPrompt>({
		defaultValues: {
			id: prompt.id,
			name: prompt.name,
			prompt: prompt.prompt ? { ...prompt.prompt, sections: defaultSections } : prompt.prompt,
			strongFitPrompt: prompt.strongFitPrompt ?? "",
			mediumFitPrompt: prompt.mediumFitPrompt ?? "",
			weakFitPrompt: prompt.weakFitPrompt ?? "",
			language: prompt.language ?? DEFAULT_LANGUAGE,
		},
		resolver: zodResolver(UpdateDeepResearchPromptSchema),
	});

	const formValues = watch();
	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];
	const containerRef = useRef<HTMLDivElement>(null);
	const wasSubmittingRef = useRef(false);

	const scrollToFirstError = () => {
		const container = containerRef.current;

		if (!container) {
			return;
		}

		const firstError = container.querySelector<HTMLElement>(
			'[class*="border-status-error"], .border-red-400'
		);

		if (!firstError) {
			return;
		}

		const containerRect = container.getBoundingClientRect();
		const errorRect = firstError.getBoundingClientRect();
		const top = errorRect.top - containerRect.top + container.scrollTop - 40;

		container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
	};

	useEffect(() => {
		if (
			wasSubmittingRef.current &&
			!isSubmitting &&
			isSubmitted &&
			Object.keys(errors).length > 0
		) {
			scrollToFirstError();
		}

		wasSubmittingRef.current = isSubmitting;
	}, [errors, isSubmitted, isSubmitting]);

	const onSubmit = async (values: UpdateDeepResearchPrompt) => {
		try {
			await updatePrompt(values).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const handleSectionsChange = (sections: Section[]) => {
		setValue("prompt.sections", sections, { shouldValidate: true, shouldDirty: true });
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			size="xl"
			submitButtonText={t("basics.confirm")}
			title="Edit Deep Research Prompt"
		>
			<div
				ref={containerRef}
				className="flex flex-col w-full gap-3 max-h-[70vh] overflow-y-auto scrollbar-hide pr-1"
			>
				<InputField
					error={!!errors.name?.message}
					handleChange={(e: ChangeEvent<HTMLInputElement>) => {
						setValue("name", e.target.value, { shouldValidate: true, shouldDirty: true });
					}}
					label="Name"
					name="name"
					placeholder="e.g. B2B Sales Signal Intelligence"
					value={formValues.name || ""}
				/>

				<SelectField
					hideEmptyValue
					handleSelect={(value) => {
						setValue("language", value as string, { shouldValidate: true, shouldDirty: true });
					}}
					label={t("templates.deepResearchPrompts.outputLanguage")}
					options={languageOptions}
					selectedOption={formValues.language ?? DEFAULT_LANGUAGE}
				/>

				<div className="flex flex-col gap-3 mt-4">
					<h3 className="text-sm font-semibold text-text">Fit Assessment Criteria</h3>
					<p className="text-xs text-text-meta -mt-2">
						Define what makes a prospect a strong, medium, or weak fit for your outreach.
					</p>
					<div className="flex flex-col gap-0">
						<TextArea
							handleChange={(e) => {
								setValue("strongFitPrompt", e.target.value, {
									shouldValidate: true,
									shouldDirty: true,
								});
							}}
							label="Strong Fit Criteria"
							name="strongFitPrompt"
							placeholder="e.g. The prospect operates in our target industry, has 50+ employees, and shows clear budget signals..."
							showError={false}
							value={formValues.strongFitPrompt || ""}
						/>
					</div>
					<div className="flex flex-col gap-0">
						<TextArea
							handleChange={(e) => {
								setValue("mediumFitPrompt", e.target.value, {
									shouldValidate: true,
									shouldDirty: true,
								});
							}}
							label="Medium Fit Criteria"
							name="mediumFitPrompt"
							placeholder="e.g. The prospect is in an adjacent industry or has some but not all ideal characteristics..."
							showError={false}
							value={formValues.mediumFitPrompt || ""}
						/>
					</div>
					<div className="flex flex-col gap-0">
						<TextArea
							handleChange={(e) => {
								setValue("weakFitPrompt", e.target.value, {
									shouldValidate: true,
									shouldDirty: true,
								});
							}}
							label="Weak Fit Criteria"
							name="weakFitPrompt"
							placeholder="e.g. The prospect shows no alignment with our solution or has no immediate need..."
							showError={false}
							value={formValues.weakFitPrompt || ""}
						/>
					</div>
				</div>

				<SectionEditor
					errors={errors.prompt?.sections as SectionFieldError[] | undefined}
					sections={formValues.prompt?.sections || []}
					onChange={handleSectionsChange}
				/>

				{renderErrorMessages(getAllErrors(error, formErrors))}
			</div>
		</Modal>
	);
};
